import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { RiGitMergeLine, RiUpload2Line } from '@remixicon/react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { SearchSelect } from '@/design-system/search-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/select';
import { toast } from '@/design-system/toaster';
import { Pitstop, getListPitstop } from '@/services/api/pitstops';
import {
  PitstopScheduleListRow,
  SlotRoster,
  pitstopSchedulesAPI,
} from '@/services/api/pitstopSchedules';
import {
  isoWeekdayOf,
  localDateTimeInZone,
  slotToLocal,
} from '@/utils/scheduleTime';
import { formatDateTimeLong } from '@/utils';
import {
  ImportAnchorRole,
  ImportCommitResult,
  ImportOriginMode,
  ImportPreviewResult,
  ImportPreviewShipmentRow,
  ImportPreviewSummary,
  ShipmentImportError,
  shipmentsAPI,
} from '@/services/api/shipments';
import { Address, addressesAPI } from '@/services/api/addresses';
import AddressFormModal from '@/pages/addresses/components/AddressFormModal';

import { HandoverBadge } from './ShipmentBadges';
import { useProvidersDropdown } from './useProvidersDropdown';

type Stage = 'pick' | 'preview' | 'committing';

// All three modes are pickable: DASH_HUB / CLIENT_PITSTOP (each with a
// pickup-or-dropoff anchor role) and DIRECT (both sides from the CSV).
type PickableOriginMode = ImportOriginMode;

/** The route an import preview is about to create, for the preview banner. */
interface PreviewRoute {
  originMode: ImportOriginMode;
  anchorRole: ImportAnchorRole;
  /** Name of the hub / pitstop anchor; null when unknown (or DIRECT). */
  anchorName: string | null;
  /** Anchor address from the dry-run echo — lets a row tell "goes to the
   *  anchor" apart from "goes to its own CSV address". */
  anchorAddress: string | null;
}

const ORIGIN_MODE_LABEL: Record<ImportOriginMode, string> = {
  DASH_HUB: 'Hub Dash',
  CLIENT_PITSTOP: 'Pitstop Client',
  DIRECT: 'Pickup Langsung',
};

/**
 * One-line "where do these goods go" banner on the preview step: mode, role and
 * the from → to direction with the anchor named, mirroring the role card ops
 * picked on the form, so a wrong Titik Pickup / Titik Dropoff pick is caught
 * before Commit. Twin of react-link-web's PreviewRouteBanner.
 */
const PreviewRouteBanner: React.FC<{ route: PreviewRoute }> = ({ route }) => {
  const direct = route.originMode === 'DIRECT';
  const dropoff = !direct && route.anchorRole === 'DESTINATION';
  const anchor =
    route.anchorName || (route.originMode === 'DASH_HUB' ? 'Hub' : 'Pitstop');
  const anchorChip =
    'rounded-md border border-accent/40 bg-accent/5 px-1.5 py-0.5 text-accent';
  const csvChip =
    'rounded-md border border-stroke-soft-200 bg-white px-1.5 py-0.5 text-text-sub-600';
  const from = direct || dropoff ? 'Alamat pickup di CSV' : anchor;
  const to = dropoff ? anchor : 'Alamat penerima di CSV';
  return (
    <div
      role="note"
      aria-label="Rute import"
      className="rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2.5"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-2 text-xs font-semibold text-text-strong-950">
          <i className="ri-route-line text-base text-accent" aria-hidden="true" />
          {ORIGIN_MODE_LABEL[route.originMode]}
          {!direct && (
            <>
              <span className="text-text-soft-400" aria-hidden="true">
                ·
              </span>
              {dropoff ? 'Titik Dropoff' : 'Titik Pickup'}
              <HandoverBadge anchorRole={route.anchorRole} />
            </>
          )}
        </p>
        <p className="flex flex-wrap items-center gap-1.5 text-[10px] font-semibold">
          <span className={dropoff || direct ? csvChip : anchorChip}>{from}</span>
          <span className="text-text-soft-400" aria-hidden="true">
            →
          </span>
          <span className={dropoff ? anchorChip : csvChip}>{to}</span>
        </p>
      </div>
      {dropoff && (
        <p className="mt-1.5 text-[11px] text-text-sub-600">
          Baris yang alamat dropoff-nya kosong di-dropoff ke{' '}
          <span className="font-semibold text-text-strong-950">{anchor}</span>;
          baris yang mengisi alamat sendiri memakai alamat di CSV.
        </p>
      )}
    </div>
  );
};

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: (result: ImportCommitResult, clientName: string) => void;
  hubs: Pitstop[];
}

const friendlyImportError = (err: unknown): string => {
  if (err instanceof ShipmentImportError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
};

const csvEscape = (value: string): string => {
  if (/[,"\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

/** One offerable slot, resolved for a specific local delivery date. */
interface SlotOption {
  id: string;
  /** `HH:mm` on the viewer's own clock — what ops sees and picks. */
  localTime: string;
  /** The same moment written in the schedule's own zone — what the API gets. */
  apiDate: string;
  apiTime: string;
  /** When the slot actually fires, for hiding the ones already gone. */
  startsAt: Date;
}

/**
 * Slots that actually run on the given LOCAL date.
 *
 * A schedule carries its own zone, and its active days are weekdays in that
 * zone — so a slot near midnight belongs to a different weekday for the viewer
 * than it does for the schedule. Translating the chosen local date back into
 * the schedule's zone before checking the weekday keeps the picker from
 * offering a day the schedule doesn't actually run, and gives the API the pair
 * it identifies the slot by.
 */
const slotsOnDate = (
  rows: PitstopScheduleListRow[],
  localDate: string
): SlotOption[] =>
  rows
    .filter((s) => s.isActive)
    .map((s) => {
      const localTime = slotToLocal(s.dispatchTime, s.timezone).time;
      const inZone = localDateTimeInZone(localDate, localTime, s.timezone);
      if (!inZone) return null;
      const weekday = isoWeekdayOf(inZone.date);
      if (weekday === null || !s.activeDays.includes(weekday)) return null;
      return {
        id: s.id,
        localTime,
        apiDate: inZone.date,
        apiTime: inZone.time,
        startsAt: new Date(`${localDate}T${localTime}:00`),
      };
    })
    .filter((slot): slot is SlotOption => slot !== null)
    .sort((a, b) => a.localTime.localeCompare(b.localTime));

/** Local tomorrow as `YYYY-MM-DD` — the default H0 (upload H-1 → dispatch H0). */
const tomorrowLocal = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const buildErrorCsv = (preview: ImportPreviewResult): Blob => {
  const header = ['row_number', 'field', 'value', 'message'];
  const lines = [header.join(',')];
  for (const e of preview.errors) {
    lines.push(
      [
        String(e.rowNumber),
        csvEscape(e.field),
        csvEscape(e.value ?? ''),
        csvEscape(e.message),
      ].join(',')
    );
  }
  return new Blob(['﻿' + lines.join('\r\n') + '\r\n'], {
    type: 'text/csv;charset=utf-8',
  });
};

/**
 * Belt-and-braces client scope. `getListPitstop` now sends the param under the
 * name core actually reads (`providerID`), so the server filters — but this
 * stays because the failure mode is silent: a param core doesn't recognise
 * comes back as every provider's warehouses, not an error. Without a local
 * check, ops can pick another client's pitstop and the import only fails at
 * the server with IMPORT_PITSTOP_MISMATCH (QA ISSUE-007).
 */
const ownedByClient = (rows: Pitstop[], clientId: string): Pitstop[] => {
  const id = Number(clientId);
  if (!Number.isFinite(id)) return rows;
  return rows.filter((p) => p.provider?.id === id);
};

const ImportModal: React.FC<ImportModalProps> = ({
  isOpen,
  onClose,
  onImported,
  hubs,
}) => {
  const [stage, setStage] = useState<Stage>('pick');
  // Where the goods start (TRD D3): DASH_HUB = client delivers to a Dash hub
  // (existing flow); CLIENT_PITSTOP = riders pick up at the client's pitstop
  // on the pitstop's dispatch schedule.
  const [originMode, setOriginMode] = useState<PickableOriginMode>('DASH_HUB');
  // Which side the hub/pitstop anchor plays (UI: "dipakai sebagai Titik
  // Pickup / Titik Dropoff"). ORIGIN = legacy; DESTINATION = retrieval (rows
  // are picked up at their CSV origin and dropped off AT the anchor).
  // Meaningless for DIRECT — forced back to ORIGIN there.
  const [anchorRole, setAnchorRole] = useState<ImportAnchorRole>('ORIGIN');
  const [hubId, setHubId] = useState<string>('');
  // Hub is server-searchable (debounced `search` param) rather than a static
  // <select>. Seeded from the parent-provided `hubs` so it's populated before
  // the first search.
  const [hubOptions, setHubOptions] = useState<Pitstop[]>(hubs);
  const [hubLoading, setHubLoading] = useState(false);
  const hubSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  // Client-pitstop picker (mode CLIENT_PITSTOP) — mirrors the hub picker but
  // is filtered by the selected client (`providerId`).
  const [pitstopId, setPitstopId] = useState<string>('');
  const [pitstopOptions, setPitstopOptions] = useState<Pitstop[]>([]);
  const [pitstopLoading, setPitstopLoading] = useState(false);
  const pitstopSearchDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [clientId, setClientId] = useState<string>(''); // form value (string for <select>)
  // BE-decided: this (mode, client) must supply Destination Lat/Long in the
  // CSV (DESTINATION_TO_CLIENT_PROVIDER_IDS) — flips the coords note below.
  const [requireDestCoords, setRequireDestCoords] = useState(false);
  // CLIENT_PITSTOP sub-choice (docs/pitstop/client-pitstop-no-schedule-v1.md):
  // false (default) = "Tanpa Jadwal" — riders still pick up at the pitstop and
  // no hub scan is needed, but ops dispatches manually (no slot sent, no
  // queue/auto-dispatch); true = "Pakai Jadwal" — the existing scheduled flow.
  const [scheduledDispatch, setScheduledDispatch] = useState(false);
  // Scheduled-dispatch slot (TRD v2 §D6) — the scheduled variant only: the
  // pitstop's active schedules feed the time chips, the date defaults to
  // tomorrow (upload H-1 → dispatch H0).
  const [schedules, setSchedules] = useState<PitstopScheduleListRow[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [schedulesError, setSchedulesError] = useState(false);
  const [dispatchSlotId, setDispatchSlotId] = useState<string>('');
  const [dispatchDate, setDispatchDate] = useState<string>(tomorrowLocal);
  // Rider pool of the picked slot (§D7 lens, GET :pitstopId/slot-roster) —
  // shown as "Rider bertugas" so ops sees who rides BEFORE uploading. An empty
  // pool blocks the preview: the BE refuses the upload anyway
  // (IMPORT_SLOT_NO_RIDER), and before this gate the failure only surfaced
  // later as a silently-unbuilt dispatch.
  const [slotRoster, setSlotRoster] = useState<SlotRoster | null>(null);
  const [slotRosterLoading, setSlotRosterLoading] = useState(false);
  const [slotRosterError, setSlotRosterError] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResult | null>(null);
  const [activeTab, setActiveTab] = useState<
    'toCreate' | 'duplicates' | 'errors'
  >('toCreate');
  const [appendConfirmOpen, setAppendConfirmOpen] = useState(false);
  // New DRAFT address being edited in place (fetched by id from the preview).
  const [editAddress, setEditAddress] = useState<Address | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);

  // Searchable + lazy-loaded provider picker. Replaces the previous one-shot
  // size=200 fetch — works for clients lists of any size and supports search.
  // `Provider.id` (integer) is the `clientId` sent to logistic-service.
  // Logistic-service does not expose a clients endpoint — see Part C.3 in the spec.
  const providers = useProvidersDropdown(isOpen);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Highlights the dropzone while a file is being dragged over it.
  const [dragActive, setDragActive] = useState(false);

  // Reset state every time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setStage('pick');
    setOriginMode('DASH_HUB');
    setAnchorRole('ORIGIN');
    setHubId('');
    setHubOptions(hubs);
    setPitstopId('');
    setPitstopOptions([]);
    setClientId('');
    setScheduledDispatch(false);
    setSchedules([]);
    setSchedulesLoading(false);
    setSchedulesError(false);
    setDispatchSlotId('');
    setDispatchDate(tomorrowLocal());
    setSlotRoster(null);
    setSlotRosterLoading(false);
    setSlotRosterError(false);
    setFile(null);
    setSubmitting(false);
    setInlineError(null);
    setPreview(null);
    setActiveTab('toCreate');
    setAppendConfirmOpen(false);
    setEditAddress(null);
    setEditLoadingId(null);
  }, [isOpen, hubs]);

  // Clean up the hub/pitstop-search debounces on unmount.
  useEffect(() => {
    return () => {
      if (hubSearchDebounceRef.current) {
        clearTimeout(hubSearchDebounceRef.current);
      }
      if (pitstopSearchDebounceRef.current) {
        clearTimeout(pitstopSearchDebounceRef.current);
      }
    };
  }, []);

  // Escape closes the append-confirm modal while it's open.
  useEffect(() => {
    if (!isOpen || !appendConfirmOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) {
        setAppendConfirmOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, appendConfirmOpen, submitting]);

  const clientIdNumeric = useMemo(() => {
    const n = Number(clientId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [clientId]);

  // labelFor reads from the hook's stable cache, so the picked client's
  // name stays visible even after a search narrows it out of the results.
  const clientName = useMemo(
    () => (clientId ? providers.labelFor(clientId) : ''),
    [providers, clientId]
  );

  // Seed the client-pitstop options as soon as the picker becomes relevant
  // (mode CLIENT_PITSTOP + a client picked). Search refinements go through
  // the debounced `handlePitstopSearch` below.
  // Ask the BE whether this (mode, client) requires Destination Lat/Long in
  // the CSV — same predicate the import validation enforces.
  useEffect(() => {
    if (!isOpen || !clientIdNumeric) {
      setRequireDestCoords(false);
      return;
    }
    let stale = false;
    shipmentsAPI
      .getImportConfig({
        originMode,
        anchorRole: originMode === 'DIRECT' ? undefined : anchorRole,
        clientId: clientIdNumeric,
      })
      .then((cfg) => {
        if (!stale) setRequireDestCoords(cfg.requireDestinationCoords);
      })
      .catch(() => {
        if (!stale) setRequireDestCoords(false);
      });
    return () => {
      stale = true;
    };
  }, [isOpen, originMode, anchorRole, clientIdNumeric]);

  useEffect(() => {
    if (!isOpen || originMode !== 'CLIENT_PITSTOP' || !clientId) {
      setPitstopOptions([]);
      return;
    }
    let stale = false;
    setPitstopLoading(true);
    getListPitstop({
      type: 'PROVIDER_WAREHOUSE',
      providerId: clientId,
      page: '1',
      size: '100',
    })
      .then((res) => {
        if (!stale) setPitstopOptions(ownedByClient(res.data ?? [], clientId));
      })
      .catch(() => {
        // Keep whatever we had; the picker shows its empty message.
      })
      .finally(() => {
        if (!stale) setPitstopLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [isOpen, originMode, clientId]);

  // Load the picked pitstop's dispatch schedules — they feed the time chips of
  // the "Jadwal Dispatch" panel. Re-picking a pitstop clears the picked time
  // (schedules belong to one pitstop).
  useEffect(() => {
    setDispatchSlotId('');
    if (
      !isOpen ||
      originMode !== 'CLIENT_PITSTOP' ||
      !scheduledDispatch ||
      !pitstopId
    ) {
      setSchedules([]);
      setSchedulesError(false);
      return;
    }
    let stale = false;
    setSchedulesLoading(true);
    setSchedulesError(false);
    pitstopSchedulesAPI
      .list(pitstopId)
      .then((rows) => {
        if (stale) return;
        setSchedules(rows);
      })
      .catch(() => {
        if (!stale) {
          setSchedules([]);
          setSchedulesError(true);
        }
      })
      .finally(() => {
        if (!stale) setSchedulesLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [isOpen, originMode, scheduledDispatch, pitstopId]);

  // The slot ops effectively picked — same derivation rule as the render-side
  // `selectedSlot` below, memoized up here so the roster effect can key on it
  // (hooks must run before the early return).
  const selectedSlotKey = useMemo(() => {
    const offerable = slotsOnDate(schedules, dispatchDate).filter(
      (s) => s.startsAt.getTime() > Date.now()
    );
    const slot =
      offerable.find((s) => s.id === dispatchSlotId) ??
      (offerable.length === 1 ? offerable[0] : null);
    return slot
      ? { id: slot.id, apiDate: slot.apiDate, apiTime: slot.apiTime }
      : null;
  }, [schedules, dispatchDate, dispatchSlotId]);

  // Load the picked slot's rider pool — the "Rider bertugas" panel. Keyed on
  // the effective slot, so it follows date/time repicks and clears when the
  // pick stops matching.
  useEffect(() => {
    if (
      !isOpen ||
      originMode !== 'CLIENT_PITSTOP' ||
      !scheduledDispatch ||
      !pitstopId ||
      !clientIdNumeric ||
      !selectedSlotKey
    ) {
      setSlotRoster(null);
      setSlotRosterError(false);
      return;
    }
    let stale = false;
    setSlotRosterLoading(true);
    setSlotRosterError(false);
    pitstopSchedulesAPI
      .slotRoster(pitstopId, {
        clientId: clientIdNumeric,
        date: selectedSlotKey.apiDate,
        time: selectedSlotKey.apiTime,
      })
      .then((r) => {
        if (!stale) setSlotRoster(r);
      })
      .catch(() => {
        if (!stale) {
          setSlotRoster(null);
          setSlotRosterError(true);
        }
      })
      .finally(() => {
        if (!stale) setSlotRosterLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [
    isOpen,
    originMode,
    scheduledDispatch,
    pitstopId,
    clientIdNumeric,
    selectedSlotKey,
  ]);

  if (!isOpen) return null;

  const fetchHubOptions = async (search: string) => {
    setHubLoading(true);
    try {
      const res = await getListPitstop({
        type: 'HUB',
        hasStaff: true,
        page: '1',
        size: '100',
        ...(search ? { search } : {}),
      });
      setHubOptions(res.data ?? []);
    } catch (_) {
      // Keep the current options on a failed search rather than blanking out.
    } finally {
      setHubLoading(false);
    }
  };

  // Debounced (500ms) server-side hub search.
  const handleHubSearch = (term: string) => {
    if (hubSearchDebounceRef.current) {
      clearTimeout(hubSearchDebounceRef.current);
    }
    hubSearchDebounceRef.current = setTimeout(() => {
      void fetchHubOptions(term.trim());
    }, 500);
  };

  // Falls back to the parent list so the picked hub's name stays visible even
  // after a search narrows it out of the fetched results.
  const selectedHub =
    hubOptions.find((h) => String(h.id) === hubId) ??
    hubs.find((h) => String(h.id) === hubId);

  const fetchPitstopOptions = async (search: string) => {
    if (!clientId) return;
    setPitstopLoading(true);
    try {
      const res = await getListPitstop({
        type: 'PROVIDER_WAREHOUSE',
        providerId: clientId,
        page: '1',
        size: '100',
        ...(search ? { search } : {}),
      });
      setPitstopOptions(ownedByClient(res.data ?? [], clientId));
    } catch (_) {
      // Keep the current options on a failed search rather than blanking out.
    } finally {
      setPitstopLoading(false);
    }
  };

  // Debounced (500ms) server-side pitstop search — mirror of handleHubSearch.
  const handlePitstopSearch = (term: string) => {
    if (pitstopSearchDebounceRef.current) {
      clearTimeout(pitstopSearchDebounceRef.current);
    }
    pitstopSearchDebounceRef.current = setTimeout(() => {
      void fetchPitstopOptions(term.trim());
    }, 500);
  };

  const selectedPitstop = pitstopOptions.find(
    (p) => String(p.id) === pitstopId
  );

  // What the preview is about to import, stated back to ops so a wrong
  // Titik Pickup / Titik Dropoff pick is caught BEFORE Commit. The dry-run echo
  // is authoritative; the form state is the fallback for older BE builds.
  const previewRoute: PreviewRoute | null = preview
    ? {
        originMode: preview.summary.originMode ?? originMode,
        anchorRole: preview.summary.anchorRole ?? anchorRole,
        anchorName:
          preview.summary.origin?.name ??
          (originMode === 'DASH_HUB'
            ? selectedHub?.name
            : originMode === 'CLIENT_PITSTOP'
              ? selectedPitstop?.name
              : undefined) ??
          null,
        anchorAddress: preview.summary.origin?.address ?? null,
      }
    : null;

  // A pitstop belongs to one client — switching client invalidates the pick.
  const handleClientChange = (next: string) => {
    setClientId(next);
    setPitstopId('');
  };

  const handleFile = (f: File | null) => {
    if (f && f.size > 5 * 1024 * 1024) {
      setInlineError('File too large. Max 5 MB.');
      return;
    }
    setFile(f);
    setInlineError(null);
  };

  const handlePreview = async () => {
    if (!clientIdNumeric || !file) return;
    setInlineError(null);
    setSubmitting(true);
    try {
      const result = await shipmentsAPI.importDryRun({
        clientId: clientIdNumeric,
        // hubId and pitstopId are mutually exclusive per mode — the BE 400s
        // (IMPORT_ORIGIN_CONFLICT) when both are sent.
        hubId: originMode === 'DASH_HUB' && hubId ? Number(hubId) : null,
        originMode,
        // Anchor role rides along for hub/pitstop; DIRECT has no anchor.
        anchorRole: originMode === 'DIRECT' ? null : anchorRole,
        pitstopId:
          originMode === 'CLIENT_PITSTOP' && pitstopId
            ? Number(pitstopId)
            : null,
        // Scheduled-dispatch slot — CLIENT_PITSTOP scheduled variant only;
        // the unscheduled variant ("Tanpa Jadwal") sends neither field.
        dispatchDate:
          originMode === 'CLIENT_PITSTOP' && scheduledDispatch
            ? (selectedSlot?.apiDate ?? null)
            : null,
        dispatchTime:
          originMode === 'CLIENT_PITSTOP' && scheduledDispatch
            ? (selectedSlot?.apiTime ?? null)
            : null,
        file,
      });
      setPreview(result);
      setStage('preview');
      const next: 'toCreate' | 'duplicates' | 'errors' =
        result.summary.errors > 0
          ? 'errors'
          : result.summary.duplicates > 0
            ? 'duplicates'
            : 'toCreate';
      setActiveTab(next);
    } catch (err) {
      const msg = friendlyImportError(err);
      if (
        err instanceof ShipmentImportError &&
        err.code === 'IMPORT_VALIDATION_FAILED' &&
        err.preview
      ) {
        setPreview(err.preview);
        setStage('preview');
        setInlineError(msg);
      } else {
        setInlineError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = () => {
    if (!preview || preview.summary.errors > 0) return;
    // Existing AWBs are auto-appended (no skip/overwrite choice). When any
    // duplicate is present, confirm first so the append behaviour is explicit.
    if (preview.summary.duplicates > 0) {
      setAppendConfirmOpen(true);
      return;
    }
    void doCommit();
  };

  const doCommit = async () => {
    if (!file || !clientIdNumeric || !preview) return;
    setAppendConfirmOpen(false);
    setStage('committing');
    setSubmitting(true);
    setInlineError(null);
    try {
      const result = await shipmentsAPI.importCommit({
        clientId: clientIdNumeric,
        hubId: originMode === 'DASH_HUB' && hubId ? Number(hubId) : null,
        originMode,
        // Anchor role rides along for hub/pitstop; DIRECT has no anchor.
        anchorRole: originMode === 'DIRECT' ? null : anchorRole,
        pitstopId:
          originMode === 'CLIENT_PITSTOP' && pitstopId
            ? Number(pitstopId)
            : null,
        // Same slot as the dry-run — the BE re-validates it against the
        // schedule master on commit (IMPORT_SCHEDULE_INVALID/PASSED). The
        // unscheduled variant sends neither field.
        dispatchDate:
          originMode === 'CLIENT_PITSTOP' && scheduledDispatch
            ? (selectedSlot?.apiDate ?? null)
            : null,
        dispatchTime:
          originMode === 'CLIENT_PITSTOP' && scheduledDispatch
            ? (selectedSlot?.apiTime ?? null)
            : null,
        file,
      });
      onImported(result, clientName);
      const parts: string[] = [];
      if (result.appendedItems > 0)
        parts.push(`${result.appendedItems} item appended`);
      if (result.skippedDuplicateItems > 0)
        parts.push(`${result.skippedDuplicateItems} skipped`);
      if (result.autoZonedCells > 0)
        parts.push(`${result.autoZonedCells} auto-zoned`);
      toast.success(
        `Imported ${result.createdShipments} shipment${
          result.createdShipments === 1 ? '' : 's'
        }${clientName ? ` for ${clientName}` : ''}`,
        { description: parts.join(' · ') || 'No items appended.' }
      );
      onClose();
    } catch (err) {
      if (
        err instanceof ShipmentImportError &&
        err.code === 'IMPORT_VALIDATION_FAILED' &&
        err.preview
      ) {
        setPreview(err.preview);
        setStage('preview');
        setInlineError(
          err.message ||
            'The file no longer matches the database state. Re-preview before committing.'
        );
      } else {
        setStage('preview');
        setInlineError(friendlyImportError(err));
        toast.error('Import failed', { description: friendlyImportError(err) });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadErrors = () => {
    if (!preview) return;
    const blob = buildErrorCsv(preview);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'shipment-import-errors.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Open the address editor for a new (DRAFT) lane surfaced in the preview.
  const handleEditAddress = async (id: string) => {
    setEditLoadingId(id);
    try {
      const address = await addressesAPI.get(id);
      setEditAddress(address);
    } catch (err) {
      toast.error('Failed to open address', {
        description: friendlyImportError(err),
      });
    } finally {
      setEditLoadingId(null);
    }
  };

  // Saved lane is now CONFIRMED — drop it from the preview's "new addresses"
  // list (and the draft count) so it reads as reviewed.
  const handleAddressSaved = (saved: Address) => {
    setEditAddress(null);
    setPreview((prev) => {
      if (!prev?.summary.addresses) return prev;
      const addr = prev.summary.addresses;
      const newAddresses = (addr.newAddresses ?? []).filter(
        (a) => a.id !== saved.id
      );
      return {
        ...prev,
        summary: {
          ...prev.summary,
          addresses: {
            ...addr,
            newAddresses,
            draft: Math.max(0, (addr.draft ?? 0) - 1),
          },
        },
      };
    });
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFile(dropped);
  };

  // dragleave also fires when the cursor crosses into a child; the very next
  // dragover on the container flips it back on, so no flicker guard needed.
  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  // Mode-aware location requirement: DASH_HUB needs a hub, CLIENT_PITSTOP
  // needs a client pitstop, DIRECT needs none (both sides from the CSV).
  // Client + file are always required.
  const hasOriginLocation =
    originMode === 'DIRECT'
      ? true
      : originMode === 'DASH_HUB'
        ? Boolean(hubId)
        : Boolean(pitstopId);

  // Scheduled-dispatch slot state (CLIENT_PITSTOP only). Ops pick the H0 date
  // first, then a time — so the chips only ever offer slots that actually run
  // on that weekday, instead of offering all of them and rejecting the pair
  // afterwards (the BE answers IMPORT_SCHEDULE_INVALID for a mismatch).
  const activeSchedules = schedules.filter((s) => s.isActive);
  const slotsForDate = slotsOnDate(schedules, dispatchDate);
  // A slot whose moment has passed can't be booked — the API answers
  // IMPORT_SCHEDULE_PASSED — so offering it only invites a dead end. Matters
  // on today's date: picking 09:15 at 10:11 is never valid. The cut is the
  // same as the API's (strictly in the future), so the picker never hides a
  // slot the API would still take.
  const schedulesOnDate = slotsForDate.filter(
    (slot) => slot.startsAt.getTime() > Date.now()
  );
  const passedCount = slotsForDate.length - schedulesOnDate.length;
  // The pick is derived, not reconciled: a stored slot that doesn't run on the
  // newly chosen date simply stops matching, and a date with exactly one slot
  // needs no click at all. So changing the date can never leave a stale time
  // selected.
  const selectedSlot =
    schedulesOnDate.find((s) => s.id === dispatchSlotId) ??
    (schedulesOnDate.length === 1 ? schedulesOnDate[0] : null);
  // No active schedules at all → preview is blocked entirely (panel links to
  // the Jadwal Dispatch page, the single source of truth for slots). The
  // unscheduled variant needs no slot — that's its point.
  const hasScheduleSlot =
    originMode !== 'CLIENT_PITSTOP' ||
    !scheduledDispatch ||
    (Boolean(selectedSlot) && Boolean(dispatchDate));

  // Rider gate (mirrors the BE's IMPORT_SLOT_NO_RIDER): a picked slot whose
  // pool is KNOWN empty blocks the preview; while the pool is still loading
  // the button waits. A fetch failure does NOT block — the BE re-checks and
  // answers with the precise reason. Scheduled variant only: without a slot
  // there is no roster to gate on (ops picks riders at manual dispatch).
  const rosterKnownEmpty =
    originMode === 'CLIENT_PITSTOP' &&
    scheduledDispatch &&
    Boolean(selectedSlot) &&
    slotRoster !== null &&
    slotRoster.riders.length === 0;
  const rosterPending =
    originMode === 'CLIENT_PITSTOP' &&
    scheduledDispatch &&
    Boolean(selectedSlot) &&
    slotRosterLoading;

  const canPreview =
    hasOriginLocation &&
    hasScheduleSlot &&
    !rosterKnownEmpty &&
    !rosterPending &&
    Boolean(clientIdNumeric) &&
    Boolean(file) &&
    !submitting &&
    stage === 'pick';

  // Shipments that will actually change: brand-new AWBs + existing AWBs that
  // gain at least one appended item. A duplicate whose items are all skipped
  // doesn't count.
  const affectedShipments = preview
    ? preview.summary.toCreateShipments + preview.summary.toAppendShipments
    : 0;

  const blockCommit =
    !preview || preview.summary.errors > 0 || affectedShipments === 0;

  const pickFooter = (
    <div className="flex items-center justify-end gap-2">
      <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button onClick={handlePreview} disabled={!canPreview}>
        {submitting ? (
          <>
            <i className="ri-loader-4-line mr-1.5 animate-spin text-[14px]" />
            Previewing…
          </>
        ) : (
          'Preview import'
        )}
      </Button>
    </div>
  );

  const previewFooter = preview && (
    <div className="flex items-center justify-end gap-2">
      <Button
        tone="neutral" style="stroke"
        onClick={() => {
          setStage('pick');
          setPreview(null);
          setInlineError(null);
        }}
        disabled={submitting}
      >
        Back
      </Button>
      <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button onClick={handleConfirm} disabled={blockCommit || submitting}>
        {submitting ? (
          <>
            <i className="ri-loader-4-line mr-1.5 animate-spin text-[14px]" />
            Importing…
          </>
        ) : (
          `Confirm import${
            affectedShipments > 0 ? ` (${affectedShipments})` : ''
          }`
        )}
      </Button>
    </div>
  );

  return (
    <>
      <FormModal
        // Hidden while editing a new address — the address editor uses the
        // legacy (non-portal) modal, which would render behind this radix
        // dialog. Preview state is preserved, so this reopens intact on close.
        open={isOpen && editAddress === null}
        onClose={onClose}
        icon={<RiUpload2Line />}
        iconTone="primary"
        title="Import shipments from CSV"
        subtitle="Upload a SwipeRX-format CSV. We'll group rows by AWB into shipments + items."
        size="2xl"
        padded={false}
        closeOnBackdropClick={!submitting}
        closeOnEscape={!submitting && !appendConfirmOpen}
        footer={stage === 'pick' ? pickFooter : previewFooter}
      >
        {stage === 'pick' && (
          <div className="space-y-5 p-5">
            {/* Mode = anchor LOCATION; its pickup/dropoff ROLE is the section
                below. DIRECT has no anchor — both sides come from the CSV. */}
            <div>
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                Barang lewat mana? <span className="text-error-base">*</span>
              </label>
              <div className="grid grid-cols-3 gap-1 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 p-1">
                <OriginModeButton
                  active={originMode === 'DASH_HUB'}
                  onClick={() => setOriginMode('DASH_HUB')}
                  disabled={submitting}
                  label="Hub Dash"
                  hint="Barang mampir / berangkat dari hub"
                />
                <OriginModeButton
                  active={originMode === 'CLIENT_PITSTOP'}
                  onClick={() => setOriginMode('CLIENT_PITSTOP')}
                  disabled={submitting}
                  label="Pitstop Client"
                  hint="Barang mampir / berangkat dari gudang client"
                />
                {/* Temporarily disabled: the DIRECT dispatch machine isn't
                    built yet, so imported items would sit CREATED with no way
                    to dispatch them. The BE intake path stays alive — re-enable
                    by restoring the onClick (setOriginMode('DIRECT') + reset
                    anchorRole to ORIGIN + scheduledDispatch to false). */}
                <OriginModeButton
                  active={false}
                  disabled
                  title="Segera hadir — dispatch untuk Pickup Langsung masih disiapkan"
                  label="Pickup Langsung"
                  hint="Segera hadir"
                />
              </div>
            </div>

            {/* Anchor role — the hub/pitstop as pickup point (legacy) or as
                dropoff/collection point (retrieval). Hidden for DIRECT. */}
            {originMode !== 'DIRECT' && (
              <div>
                <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                  {originMode === 'DASH_HUB' ? 'Hub' : 'Pitstop'} dipakai
                  sebagai <span className="text-error-base">*</span>
                </label>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <AnchorRoleCard
                    active={anchorRole === 'ORIGIN'}
                    onClick={() => setAnchorRole('ORIGIN')}
                    disabled={submitting}
                    title="Titik Pickup"
                    anchorRole="ORIGIN"
                    fromLabel={originMode === 'DASH_HUB' ? 'Hub' : 'Pitstop'}
                    toLabel="Alamat dropoff"
                    description={`Barang di-pickup rider dari ${
                      originMode === 'DASH_HUB' ? 'hub' : 'pitstop'
                    }, lalu di-dropoff ke alamat penerima di file CSV.`}
                  />
                  <AnchorRoleCard
                    active={anchorRole === 'DESTINATION'}
                    onClick={() => {
                      setAnchorRole('DESTINATION');
                      // Retrieval has no departure slot — the schedule
                      // sub-choice only applies to the pickup role.
                      setScheduledDispatch(false);
                    }}
                    disabled={submitting}
                    isNew
                    title="Titik Dropoff"
                    anchorRole="DESTINATION"
                    fromLabel="Alamat pickup"
                    toLabel={originMode === 'DASH_HUB' ? 'Hub' : 'Pitstop'}
                    description={`Rider pickup barang di alamat-alamat pengirim di file CSV, lalu semuanya di-dropoff ke ${
                      originMode === 'DASH_HUB' ? 'hub' : 'pitstop'
                    }.`}
                  />
                </div>
              </div>
            )}

            {/* Client first — the pitstop picker filters by the picked client. */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="import-client"
                  className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
                >
                  Client <span className="text-error-base">*</span>
                </label>
                <SearchSelect
                  placeholder="Search or pick a client…"
                  options={providers.options.map((p) => ({
                    value: p.id,
                    label: p.name,
                  }))}
                  value={clientId}
                  selectedLabelFallback={providers.labelFor(clientId)}
                  onChange={(next) => handleClientChange(String(next))}
                  onSearchChange={providers.setSearch}
                  disableLocalFilter
                  loading={providers.loading}
                  hasMore={providers.hasMore}
                  onLoadMore={providers.loadMore}
                  disabled={submitting}
                  emptyMessage={providers.error ?? 'No clients found'}
                />
                {providers.error && (
                  <p className="mt-1 text-[11px] text-error-base">
                    {providers.error}
                  </p>
                )}
              </div>
              {originMode === 'DIRECT' ? (
                <div>
                  <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                    Titik pickup &amp; dropoff
                  </label>
                  <div className="rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2 text-sm text-text-sub-600">
                    Sesuai alamat di file CSV
                  </div>
                  <p className="mt-1 text-[11px] leading-relaxed text-text-sub-600">
                    Tidak perlu pilih hub/pitstop. Satu file boleh berisi
                    beberapa alamat pickup.
                  </p>
                </div>
              ) : originMode === 'DASH_HUB' ? (
                <div>
                  <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                    Hub <span className="text-error-base">*</span>
                  </label>
                  <SearchSelect
                    placeholder="Search or pick a hub…"
                    options={hubOptions.map((h) => ({
                      value: h.id,
                      label: h.name,
                    }))}
                    value={hubId}
                    selectedLabelFallback={selectedHub?.name}
                    onChange={(next) => setHubId(String(next))}
                    onSearchChange={handleHubSearch}
                    disableLocalFilter
                    loading={hubLoading}
                    disabled={submitting}
                    emptyMessage="No hubs found"
                  />
                  <p className="mt-1 text-[11px] leading-relaxed text-text-sub-600">
                    {anchorRole === 'DESTINATION'
                      ? 'Semua barang di file ini akan dikumpulkan (di-dropoff) ke hub ini.'
                      : 'Semua barang di file ini berangkat dari hub ini.'}
                  </p>
                </div>
              ) : (
                <div>
                  <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                    Pitstop client <span className="text-error-base">*</span>
                  </label>
                  <SearchSelect
                    placeholder={
                      clientId
                        ? 'Cari atau pilih pitstop…'
                        : 'Pilih client terlebih dahulu'
                    }
                    options={pitstopOptions.map((p) => ({
                      value: p.id,
                      label: p.name,
                    }))}
                    value={pitstopId}
                    selectedLabelFallback={selectedPitstop?.name}
                    onChange={(next) => setPitstopId(String(next))}
                    onSearchChange={handlePitstopSearch}
                    disableLocalFilter
                    loading={pitstopLoading}
                    disabled={submitting || !clientId}
                    emptyMessage="Client ini belum punya pitstop"
                  />
                  <p className="mt-1 text-[11px] leading-relaxed text-text-sub-600">
                    {anchorRole === 'DESTINATION'
                      ? 'Semua barang di file ini akan dikumpulkan (di-dropoff) ke pitstop ini.'
                      : scheduledDispatch
                        ? 'Barang diambil rider di pitstop ini sesuai jadwal dispatch.'
                        : 'Barang diambil rider di pitstop ini; dispatch dibuat manual oleh ops.'}
                  </p>
                </div>
              )}
            </div>

            {/* CLIENT_PITSTOP sub-choice: unscheduled (default) vs scheduled
                (docs/pitstop/client-pitstop-no-schedule-v1.md). Both are
                rider-pickup-at-pitstop without a hub scan — the choice is only
                about WHEN the dispatch is made. */}
            {originMode === 'CLIENT_PITSTOP' && anchorRole === 'ORIGIN' && (
              <div>
                <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                  Proses dispatch <span className="text-error-base">*</span>
                </label>
                <div className="grid grid-cols-2 gap-1 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 p-1">
                  <OriginModeButton
                    active={!scheduledDispatch}
                    onClick={() => setScheduledDispatch(false)}
                    disabled={submitting}
                    label="Tanpa Jadwal"
                    hint="Rider jemput, dispatch manual oleh ops"
                  />
                  <OriginModeButton
                    active={scheduledDispatch}
                    onClick={() => setScheduledDispatch(true)}
                    disabled={submitting}
                    label="Pakai Jadwal"
                    hint="Auto-dispatch sesuai jadwal pitstop"
                  />
                </div>
              </div>
            )}

            {/* Unscheduled variant — no slot to pick; just say what happens
                instead so ops doesn't wait for an auto-dispatch. */}
            {originMode === 'CLIENT_PITSTOP' && anchorRole === 'ORIGIN' && !scheduledDispatch && (
              <div className="flex items-start gap-2 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2.5">
                <i
                  className="ri-information-line mt-px shrink-0 text-[14px] text-text-sub-600"
                  aria-hidden="true"
                />
                <p className="text-[11px] leading-relaxed text-text-sub-600">
                  <span className="font-semibold text-text-strong-950">
                    Tanpa jadwal dispatch.
                  </span>{' '}
                  Rider tetap jemput di pitstop client dan barang tidak perlu
                  scan masuk hub — item langsung siap dispatch begitu import
                  selesai. Tidak ada auto-dispatch: ops membuat dispatch manual
                  dari halaman Dispatches saat barang siap.
                </p>
              </div>
            )}

            {/* Jadwal Dispatch panel (TRD v2 §D8.1, scheduled variant) —
                appears once a pitstop is picked: pick one active time slot +
                the H0 delivery date. The pair is converted to UTC before it
                reaches the BE. */}
            {originMode === 'CLIENT_PITSTOP' && anchorRole === 'ORIGIN' && scheduledDispatch && pitstopId && (
              <div className="rounded-lg border border-stroke-soft-200 bg-bg-weak-50 p-4">
                <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                  Jadwal Dispatch <span className="text-error-base">*</span>
                </label>
                {schedulesLoading ? (
                  <p className="py-2 text-xs text-text-sub-600">
                    <i className="ri-loader-4-line mr-1.5 animate-spin" />
                    Memuat jadwal pitstop…
                  </p>
                ) : schedulesError ? (
                  <p className="text-xs text-error-base">
                    Gagal memuat jadwal pitstop — coba pilih ulang pitstop atau
                    refresh halaman.
                  </p>
                ) : activeSchedules.length === 0 ? (
                  <p className="text-xs text-text-sub-600">
                    Pitstop ini belum punya jadwal dispatch — atur di{' '}
                    <Link
                      to="/dispatch-schedules"
                      className="font-semibold text-accent hover:underline"
                    >
                      halaman Jadwal Dispatch
                    </Link>
                    . Upload diblokir sampai ada jadwal aktif.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-end gap-4">
                      <div>
                        <label
                          htmlFor="import-dispatch-date"
                          className="mb-1.5 block text-[11px] text-text-sub-600"
                        >
                          1. Tanggal kirim (H0){' '}
                          <span className="text-error-base">*</span>
                        </label>
                        <input
                          id="import-dispatch-date"
                          type="date"
                          value={dispatchDate}
                          onChange={(e) => setDispatchDate(e.target.value)}
                          disabled={submitting}
                          className="h-9 rounded-lg border border-stroke-soft-200 bg-white px-3 font-mono text-sm tabular-nums text-text-strong-950 outline-none transition-colors focus:border-accent disabled:opacity-50"
                        />
                      </div>
                      <p className="pb-2 text-[11px] leading-relaxed text-text-sub-600">
                        <i
                          className="ri-flashlight-line mr-1"
                          aria-hidden="true"
                        />
                        Upload H-1 → dispatch otomatis H0 (jam terpilih).
                      </p>
                    </div>

                    <div>
                      <label
                        htmlFor="import-dispatch-time"
                        className="mb-1.5 block text-[11px] text-text-sub-600"
                      >
                        2. Jam dispatch <span className="text-error-base">*</span>
                      </label>
                      {schedulesOnDate.length === 0 ? (
                        <p className="text-[11px] text-error-base">
                          <i
                            className="ri-error-warning-line mr-1"
                            aria-hidden="true"
                          />
                          {passedCount > 0
                            ? `Semua jam dispatch hari ini sudah lewat (${passedCount} jam). Pilih tanggal berikutnya.`
                            : 'Tidak ada jadwal dispatch di hari itu. Pilih tanggal lain, atau tambah jadwal di halaman Jadwal Dispatch.'}
                        </p>
                      ) : (
                        <Select
                          value={selectedSlot?.id ?? ''}
                          onValueChange={setDispatchSlotId}
                          disabled={submitting}
                        >
                          <SelectTrigger
                            id="import-dispatch-time"
                            aria-label="Jam dispatch"
                            className="w-[200px] font-mono tabular-nums"
                          >
                            <SelectValue placeholder="Pilih jam…" />
                          </SelectTrigger>
                          <SelectContent>
                            {schedulesOnDate.map((slot) => (
                              <SelectItem
                                key={slot.id}
                                value={slot.id}
                                className="font-mono tabular-nums"
                              >
                                {slot.localTime}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {/* Rider bertugas — the §D7 pool of the picked slot. An
                        empty pool blocks the upload here AND at the BE
                        (IMPORT_SLOT_NO_RIDER); before this, an ill-ridered
                        slot only failed later as a silently-unbuilt dispatch. */}
                    {selectedSlot && (
                      <div>
                        <label className="mb-1.5 block text-[11px] text-text-sub-600">
                          3. Rider bertugas
                        </label>
                        {slotRosterLoading ? (
                          <p className="text-[11px] text-text-sub-600">
                            <i
                              className="ri-loader-4-line mr-1 animate-spin"
                              aria-hidden="true"
                            />
                            Memuat rider yang bertugas…
                          </p>
                        ) : slotRosterError ? (
                          <p className="text-[11px] text-warning-base">
                            <i
                              className="ri-error-warning-line mr-1"
                              aria-hidden="true"
                            />
                            Gagal memuat daftar rider — upload tetap dicek
                            server: slot tanpa rider akan ditolak.
                          </p>
                        ) : slotRoster && slotRoster.riders.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {slotRoster.riders.map((r) => (
                              <span
                                key={r.id}
                                className="inline-flex items-center gap-1.5 rounded-full border border-stroke-soft-200 bg-white px-2.5 py-1 text-[11px] font-medium text-text-strong-950"
                              >
                                <i
                                  className="ri-user-3-line text-accent"
                                  aria-hidden="true"
                                />
                                {r.name}
                                <span className="font-mono text-[10px] font-normal text-text-sub-600">
                                  {r.code}
                                </span>
                              </span>
                            ))}
                          </div>
                        ) : slotRoster ? (
                          <p className="text-[11px] leading-relaxed text-error-base">
                            <i
                              className="ri-error-warning-line mr-1"
                              aria-hidden="true"
                            />
                            {slotRoster.reason === 'NO_CALENDAR_SLOT' ? (
                              <>
                                Belum ada jadwal rider yang mencakup jam ini —
                                generate slot-nya dulu di halaman{' '}
                                <Link
                                  to="/scheduled-instant"
                                  className="font-semibold underline"
                                >
                                  Scheduled Instant
                                </Link>
                                . Upload diblokir.
                              </>
                            ) : slotRoster.reason === 'NO_ZONE_DRIVERS' ? (
                              <>
                                Pitstop ini belum punya mitra di zone aktif mana
                                pun — atur mitra di halaman{' '}
                                <Link
                                  to="/zones"
                                  className="font-semibold underline"
                                >
                                  Zones
                                </Link>
                                . Upload diblokir.
                              </>
                            ) : (
                              <>
                                Tidak ada rider utama yang siap di slot ini —
                                cek penugasan/konfirmasi rider di halaman{' '}
                                <Link
                                  to="/scheduled-instant"
                                  className="font-semibold underline"
                                >
                                  Scheduled Instant
                                </Link>
                                . Upload diblokir.
                              </>
                            )}
                          </p>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                  CSV file <span className="text-error-base">*</span>
                </label>
                <button
                  type="button"
                  onClick={() =>
                    void shipmentsAPI.downloadTemplate(
                      originMode,
                      originMode === 'DIRECT' ? undefined : anchorRole
                    )
                  }
                  disabled={!clientId}
                  title={
                    clientId ? undefined : 'Pilih client dulu untuk mengunduh template'
                  }
                  className={
                    clientId
                      ? 'text-xs font-semibold tracking-tight text-accent hover:underline'
                      : 'cursor-not-allowed text-xs font-semibold tracking-tight text-text-soft-400'
                  }
                  aria-label="Download CSV template"
                >
                  Download template
                </button>
              </div>
              {/* Whole zone is the click target while empty; once a file is
                  picked it becomes a card with its own Ganti/Hapus actions
                  (dropping a new file still replaces it either way). */}
              <div
                role="button"
                tabIndex={file ? -1 : 0}
                onClick={() => {
                  if (!file && !submitting) fileInputRef.current?.click();
                }}
                onKeyDown={(e) => {
                  if (!file && !submitting && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragEnter={onDragOver}
                onDragLeave={onDragLeave}
                className={`rounded-xl border-2 border-dashed text-center transition-colors ${
                  dragActive
                    ? 'border-accent bg-accent/5'
                    : file
                      ? 'border-stroke-soft-200 bg-white'
                      : 'cursor-pointer border-stroke-soft-200 bg-bg-weak-50 hover:border-accent/60 hover:bg-white'
                } ${file ? 'px-4 py-3.5' : 'px-4 py-8'}`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv,application/vnd.ms-excel"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                />
                {file ? (
                  <div className="flex items-center justify-between gap-3 text-left">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-verified-light bg-verified-lighter">
                        <i
                          className="ri-file-text-line text-[20px] text-verified-base"
                          aria-hidden="true"
                        />
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-text-strong-950">
                          {file.name}
                        </p>
                        <p className="mt-0.5 text-xs text-text-sub-600">
                          <i
                            className="ri-checkbox-circle-fill mr-1 text-verified-base"
                            aria-hidden="true"
                          />
                          {(file.size / 1024).toFixed(1)} KB · siap di-preview
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                        disabled={submitting}
                        className="rounded-md border border-stroke-soft-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-text-sub-600 transition-colors hover:text-text-strong-950 disabled:opacity-50"
                      >
                        Ganti file
                      </button>
                      <button
                        type="button"
                        aria-label="Hapus file"
                        title="Hapus file"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFile(null);
                        }}
                        disabled={submitting}
                        className="flex size-8 items-center justify-center rounded-md text-text-sub-600 transition-colors hover:bg-error-lighter hover:text-error-base disabled:opacity-50"
                      >
                        <i
                          className="ri-delete-bin-line text-[15px]"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="pointer-events-none flex flex-col items-center gap-2.5">
                    <span
                      className={`flex size-11 items-center justify-center rounded-full border transition-colors ${
                        dragActive
                          ? 'border-accent/40 bg-accent/10'
                          : 'border-stroke-soft-200 bg-white shadow-sm'
                      }`}
                    >
                      <i
                        className={`ri-upload-cloud-2-line text-[22px] ${
                          dragActive ? 'text-accent' : 'text-text-sub-600'
                        }`}
                        aria-hidden="true"
                      />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-text-strong-950">
                        {dragActive ? (
                          'Lepas file di sini'
                        ) : (
                          <>
                            Drag &amp; drop file CSV, atau{' '}
                            <span className="font-semibold text-accent">
                              pilih file
                            </span>
                          </>
                        )}
                      </p>
                      <p className="mt-0.5 text-xs text-text-sub-600">
                        Format .csv · maks 5 MB
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {originMode !== 'DIRECT' && anchorRole === 'DESTINATION' ? (
                <p className="mt-2 text-[11px] text-text-sub-600">
                  <i className="ri-information-line mr-1" aria-hidden="true" />
                  <span className="font-semibold text-text-strong-950">
                    Yang wajib ada di CSV: alamat pickup di tiap baris
                  </span>{' '}
                  (tempat rider menjemput barang). Kolom alamat dropoff boleh
                  dikosongkan — baris yang kosong otomatis di-dropoff ke{' '}
                  {originMode === 'DASH_HUB' ? 'hub' : 'pitstop'} yang dipilih;
                  baris yang mengisi alamat dropoff sendiri memakai alamat di
                  CSV. Semua barang di file ini jadi satu rombongan pickup.
                </p>
              ) : originMode === 'CLIENT_PITSTOP' ? (
                <p className="mt-2 text-[11px] text-text-sub-600">
                  <i className="ri-information-line mr-1" aria-hidden="true" />
                  Alamat pickup diambil dari pitstop client yang dipilih
                  selama kolom Origin di CSV kosong; baris yang mengisi Origin
                  Lat/Long sendiri memakai koordinat CSV-nya (Origin Notes
                  tetap dipakai).{' '}
                  {scheduledDispatch
                    ? 'Auto-dispatch mengikuti jadwal pitstop (lihat halaman Jadwal Dispatch).'
                    : 'Tanpa auto-dispatch: ops membuat dispatch manual dari pitstop saat barang siap.'}
                </p>
              ) : originMode === 'DIRECT' ? (
                <p className="mt-2 text-[11px] text-text-sub-600">
                  <i className="ri-information-line mr-1" aria-hidden="true" />
                  <span className="font-semibold text-text-strong-950">
                    Yang wajib ada di CSV: alamat pickup &amp; alamat dropoff
                    di tiap baris.
                  </span>{' '}
                  Barang tidak mampir hub — rider pickup langsung di alamat
                  pengirim lalu dropoff ke penerima. Catatan: dispatch untuk
                  barang Pickup Langsung masih disiapkan; setelah import,
                  barang menunggu tanpa bisa di-dispatch dulu.
                </p>
              ) : (
                <p className="mt-2 text-[11px] text-text-sub-600">
                  Required: Destination Address, Receiver Name, Item Name,
                  Total Weight. Origin Address is optional when a hub is
                  selected. Origin/Destination Notes are optional free text
                  shown to the rider. See template for the full set.
                </p>
              )}
              {/* Coords note follows the BE's per-(mode, client) rule: for
                  destination-to-client uploads lat/long ARE required (their
                  destinations are end-customers — never taken from masterdata,
                  never geocoded); everyone else keeps the relaxed note. */}
              {requireDestCoords ? (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-error-light bg-error-lighter px-3 py-2">
                  <i
                    className="ri-map-pin-line mt-px shrink-0 text-[14px] text-error-base"
                    aria-hidden="true"
                  />
                  <p className="text-[11px] leading-relaxed text-error-base">
                    <span className="font-semibold">
                      Destination Lat &amp; Long wajib untuk client ini.
                    </span>{' '}
                    Alamat tujuan adalah alamat end-customer — tidak diambil
                    dari data alamat tersimpan dan tidak di-geocode. Pastikan
                    kolom Destination Lat &amp; Destination Long terisi di CSV
                    (Destination Address tetap wajib).
                  </p>
                </div>
              ) : (
                <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <i
                    className="ri-map-pin-line mt-px shrink-0 text-[14px] text-amber-600"
                    aria-hidden="true"
                  />
                  <p className="text-[11px] leading-relaxed text-amber-800">
                    <span className="font-semibold">
                      Origin &amp; Destination lat/long tidak wajib lagi.
                    </span>{' '}
                    Koordinat akan diambil otomatis dari data alamat (addresses)
                    yang tersimpan, atau di-geocode dari alamat yang kamu isi.
                    Cukup pastikan alamatnya jelas &amp; lengkap.
                  </p>
                </div>
              )}
            </div>

            {inlineError && (
              <div className="rounded-lg border border-error-light bg-error-lighter px-3 py-2 text-xs text-error-base">
                {inlineError}
              </div>
            )}
          </div>
        )}

        {(stage === 'preview' || stage === 'committing') && preview && (
          <div className="space-y-4 p-5">
            {previewRoute && <PreviewRouteBanner route={previewRoute} />}
            <PreviewSummary preview={preview} clientName={clientName} />

            {/* Echo of the validated auto-dispatch slot (TRD v2 §D6) — or,
                for the unscheduled variant, a neutral note so ops doesn't
                wait for an auto-dispatch that will never come. */}
            {preview.summary.dispatchScheduledAt ? (
              <div className="rounded-lg border border-verified-light bg-verified-lighter px-3 py-2 text-xs text-verified-base">
                <i className="ri-calendar-schedule-line mr-1.5" />
                Auto-dispatch:{' '}
                <strong>
                  {formatDateTimeLong(preview.summary.dispatchScheduledAt)} WIB
                </strong>{' '}
                — dispatch-nya dibuat sekarang juga begitu upload selesai;
                upload berikutnya untuk jam yang sama masuk ke dispatch itu.
                Commit tepat di jam ini.
              </div>
            ) : preview.summary.originMode === 'CLIENT_PITSTOP' ? (
              <div className="rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2 text-xs text-text-sub-600">
                <i className="ri-information-line mr-1.5" />
                <strong className="text-text-strong-950">
                  Tanpa jadwal dispatch
                </strong>{' '}
                — item langsung siap dispatch di
                {preview.summary.origin?.name
                  ? ` ${preview.summary.origin.name}`
                  : ' pitstop client'}{' '}
                (tanpa scan hub). Ops membuat dispatch manual dari halaman
                Dispatches saat barang siap.
              </div>
            ) : null}

            <ResolutionSummary
              summary={preview.summary}
              onEditAddress={handleEditAddress}
              editingId={editLoadingId}
            />

            {preview.summary.hubConflict > 0 && (
              <div className="rounded-gsm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                <i className="ri-error-warning-line mr-1.5" />
                <strong className="tabular-nums">
                  {preview.summary.hubConflict}
                </strong>{' '}
                destination masuk ke zona hub lain. Cek lagi hub yang dipilih
                sebelum commit.
              </div>
            )}

            {preview.summary.ignoredColumns.length > 0 && (
              <div className="rounded-lg border border-warning-light bg-warning-lighter px-3 py-2 text-xs text-warning-dark">
                Kolom tak dikenal diabaikan:{' '}
                <span className="font-mono">
                  {preview.summary.ignoredColumns.join(', ')}
                </span>
              </div>
            )}

            <div className="flex items-center gap-1 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 p-1">
              <PreviewTab
                active={activeTab === 'toCreate'}
                onClick={() => setActiveTab('toCreate')}
                label="To create"
                count={preview.summary.toCreateShipments}
                tone="emerald"
              />
              <PreviewTab
                active={activeTab === 'duplicates'}
                onClick={() => setActiveTab('duplicates')}
                label="Duplicates"
                count={preview.summary.duplicates}
                tone="amber"
              />
              <PreviewTab
                active={activeTab === 'errors'}
                onClick={() => setActiveTab('errors')}
                label="Errors"
                count={preview.summary.errors}
                tone="red"
              />
            </div>

            <div className="overflow-hidden rounded-lg border border-stroke-soft-200">
              {activeTab === 'toCreate' && (
                <ToCreateTable preview={preview} route={previewRoute} />
              )}
              {activeTab === 'duplicates' && (
                <DuplicatesTable preview={preview} />
              )}
              {activeTab === 'errors' && (
                <ErrorsTable
                  preview={preview}
                  onDownload={handleDownloadErrors}
                />
              )}
            </div>

            {inlineError && (
              <div className="rounded-lg border border-error-light bg-error-lighter px-3 py-2 text-xs text-error-base">
                {inlineError}
              </div>
            )}
          </div>
        )}
      </FormModal>

      {/* Nested append-confirm modal — stacks on top naturally. */}
      {preview && (
        <FormModal
          open={appendConfirmOpen}
          onClose={() => !submitting && setAppendConfirmOpen(false)}
          icon={<RiGitMergeLine />}
          iconTone="warning"
          title={`${preview.summary.duplicates} booking ID${
            preview.summary.duplicates === 1 ? '' : 's'
          } already exist${preview.summary.duplicates === 1 ? 's' : ''}.`}
          subtitle="New items are appended to the existing shipments. Already-present items are skipped."
          size="sm"
          closeOnBackdropClick={!submitting}
          closeOnEscape={!submitting}
          footer={
            <div className="flex items-center justify-end gap-2">
              <Button
                tone="neutral" style="stroke"
                onClick={() => setAppendConfirmOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button onClick={() => void doCommit()} disabled={submitting}>
                Append &amp; import
              </Button>
            </div>
          }
        >
          <ul className="space-y-2 text-xs text-text-sub-600">
            <li>
              <strong>{preview.summary.toCreateShipments}</strong> new shipment
              {preview.summary.toCreateShipments === 1 ? '' : 's'} created,{' '}
              <strong>{preview.summary.toAppendItems}</strong> item
              {preview.summary.toAppendItems === 1 ? '' : 's'} appended to the
              existing AWBs.
            </li>
            {preview.summary.skippedDuplicateItems > 0 && (
              <li>
                <strong>{preview.summary.skippedDuplicateItems}</strong> row
                {preview.summary.skippedDuplicateItems === 1 ? '' : 's'} skipped
                — their item name already exists on the shipment.
              </li>
            )}
            <li className="text-text-sub-600">
              An AWB that gains new items re-opens — its status is a rollup of
              its items, so a <strong>Dispatched</strong> one drops back to{' '}
              <strong>Partially dispatched</strong> and a{' '}
              <strong>Completed</strong> one back to <strong>At hub</strong>{' '}
              until the new items ship. Cancelled AWBs are never resurrected.
            </li>
          </ul>
        </FormModal>
      )}

      {/* Edit a new (DRAFT) address in place, without leaving the import flow. */}
      <AddressFormModal
        isOpen={editAddress !== null}
        address={editAddress}
        onClose={() => setEditAddress(null)}
        onSaved={handleAddressSaved}
      />
    </>
  );
};

// One option of the 3-way origin-mode segmented control. Layout mirrors the
// link-web ImportModal control (rounded box, p-1, equal columns); the
// disabled option carries `title` so hover explains "Segera hadir".
const OriginModeButton: React.FC<{
  active: boolean;
  label: string;
  hint: string;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}> = ({ active, label, hint, onClick, disabled, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`rounded-md border px-3 py-2 text-left transition-colors ${
      active
        ? 'border-stroke-soft-200 bg-white text-text-strong-950 shadow-sm'
        : onClick && !disabled
          ? 'border-transparent text-text-sub-600 hover:text-text-strong-950'
          : 'cursor-not-allowed border-transparent text-text-soft-400'
    }`}
  >
    <span className="block text-xs font-semibold tracking-tight">
      {label}
    </span>
    <span
      className={`mt-0.5 block text-[10px] leading-tight ${
        active ? 'text-text-sub-600' : 'text-text-soft-400'
      }`}
    >
      {hint}
    </span>
  </button>
);

/**
 * Pickup-vs-dropoff role card — richer than OriginModeButton because the
 * direction needs a picture: a small "from → to" chip row plus a sentence.
 */
const AnchorRoleCard: React.FC<{
  active: boolean;
  title: string;
  /** Which handover the pick makes — the same tag the Shipments list shows. */
  anchorRole: ImportAnchorRole;
  fromLabel: string;
  toLabel: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
  isNew?: boolean;
}> = ({
  active,
  title,
  anchorRole,
  fromLabel,
  toLabel,
  description,
  onClick,
  disabled,
  isNew,
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`rounded-lg border p-3 text-left transition-colors ${
      active
        ? 'border-accent bg-white shadow-sm ring-1 ring-accent'
        : 'border-stroke-soft-200 bg-white text-text-sub-600 hover:border-stroke-strong-950/20'
    } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
  >
    <span className="flex items-center gap-2 text-xs font-semibold tracking-tight text-text-strong-950">
      {title}
      <HandoverBadge anchorRole={anchorRole} />
      {isNew && (
        <span className="rounded-full bg-orange-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-orange-600 ring-1 ring-inset ring-orange-200">
          Baru
        </span>
      )}
    </span>
    <span className="mt-1.5 flex items-center gap-1.5 text-[10px] font-semibold">
      <span
        className={`rounded-md border px-1.5 py-0.5 ${
          active ? 'border-accent/40 bg-accent/5 text-accent' : 'border-stroke-soft-200 text-text-sub-600'
        }`}
      >
        {fromLabel}
      </span>
      <span className="text-text-soft-400">→</span>
      <span
        className={`rounded-md border px-1.5 py-0.5 ${
          active ? 'border-accent/40 bg-accent/5 text-accent' : 'border-stroke-soft-200 text-text-sub-600'
        }`}
      >
        {toLabel}
      </span>
    </span>
    <span className="mt-1.5 block text-[10px] leading-relaxed text-text-sub-600">
      {description}
    </span>
  </button>
);

// Coordinate-resolution + hub-zone rollup from the dry-run summary. Guarded so
// it degrades gracefully if a backend build predates these fields.
const ResolutionSummary: React.FC<{
  summary: ImportPreviewSummary;
  onEditAddress?: (id: string) => void;
  editingId?: string | null;
}> = ({ summary, onEditAddress, editingId }) => {
  const addr = summary.addresses;
  const hasHubStats =
    (summary.hubResolved ?? 0) +
      (summary.hubUnresolved ?? 0) +
      (summary.hubConflict ?? 0) >
    0;
  if (!addr && !hasHubStats) return null;

  return (
    <div className="space-y-1.5 rounded-gsm border border-gray-200 bg-gray-50 px-4 py-3">
      {addr && (
        <p className="text-xs text-gray-700">
          <i className="ri-map-pin-2-line mr-1.5 text-primary" />
          <strong className="tabular-nums">{addr.total}</strong> alamat
          diproses —{' '}
          <span className="font-semibold tabular-nums text-emerald-700">
            {addr.resolved} berhasil
          </span>{' '}
          ({addr.created} baru, {addr.reused} dari cache)
          {addr.unresolved > 0 && (
            <>
              ,{' '}
              <span className="font-semibold tabular-nums text-red-700">
                {addr.unresolved} gagal
              </span>
            </>
          )}
        </p>
      )}
      {hasHubStats && (
        <p className="text-xs text-gray-700">
          <i className="ri-focus-3-line mr-1.5 text-primary" />
          Zona hub:{' '}
          <span className="font-semibold tabular-nums">
            {summary.hubResolved}
          </span>{' '}
          di dalam zona
          {summary.hubUnresolved > 0 && (
            <>
              {' '}
              ·{' '}
              <span className="font-semibold tabular-nums text-amber-700">
                {summary.hubUnresolved}
              </span>{' '}
              di luar zona (auto-attach saat commit)
            </>
          )}
          {summary.hubConflict > 0 && (
            <>
              {' '}
              ·{' '}
              <span className="font-semibold tabular-nums text-red-700">
                {summary.hubConflict}
              </span>{' '}
              di zona hub lain
            </>
          )}
        </p>
      )}
      {addr && ((addr.draft ?? 0) > 0 || (addr.newAddresses?.length ?? 0) > 0) && (
        <div className="mt-1 rounded-gsm border border-amber-300 bg-amber-50 px-3 py-2.5">
          <p className="flex items-start gap-1.5 text-[13px] font-bold text-amber-800">
            <i className="ri-alert-line mt-px text-[15px]" />
            <span>
              {addr.draft || addr.newAddresses?.length || 0} address baru — wajib
              dicek dulu sebelum commit
            </span>
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-amber-700">
            Koordinatnya di-resolve otomatis (masterdata/geocode) dan bisa jadi
            belum akurat. Tinjau &amp; perbaiki tiap address lewat tombol{' '}
            <b>Edit</b> sebelum lanjut commit.
          </p>
          {(addr.newAddresses?.length ?? 0) > 0 && (
            <ul className="mt-2 space-y-1">
              {addr.newAddresses.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between gap-2 rounded-gsm bg-white/60 px-2 py-1 text-[11px] text-gray-700"
                >
                  <span className="min-w-0 truncate">
                    <span className="font-medium text-gray-900">
                      {a.originName}
                    </span>{' '}
                    → {a.destinationName}
                  </span>
                  {onEditAddress && (
                    <button
                      type="button"
                      onClick={() => onEditAddress(a.id)}
                      disabled={editingId === a.id}
                      className="inline-flex shrink-0 items-center gap-1 rounded-gsm border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-amber-800 transition-colors hover:bg-amber-100 disabled:opacity-50"
                    >
                      <i
                        className={`text-[12px] ${
                          editingId === a.id
                            ? 'ri-loader-4-line animate-spin'
                            : 'ri-edit-line'
                        }`}
                      />
                      {editingId === a.id ? 'Membuka…' : 'Cek & Edit'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

interface PreviewSummaryProps {
  preview: ImportPreviewResult;
  clientName: string;
}

const PreviewSummary: React.FC<PreviewSummaryProps> = ({
  preview,
  clientName,
}) => {
  const cards: { label: string; value: number; tone: string }[] = [
    {
      label: 'New shipments',
      value: preview.summary.toCreateShipments,
      tone: 'text-success-base bg-success-lighter border-success-light',
    },
    {
      label: 'New items',
      value: preview.summary.toCreateItems,
      tone: 'text-verified-base bg-verified-lighter border-verified-light',
    },
    {
      label: 'Appended items',
      value: preview.summary.toAppendItems,
      tone: 'text-warning-dark bg-warning-lighter border-warning-light',
    },
    {
      label: 'Errors',
      value: preview.summary.errors,
      tone: 'text-error-base bg-error-lighter border-error-light',
    },
  ];
  return (
    <div>
      <p className="mb-2 text-xs text-text-sub-600">
        {preview.summary.totalRows} row
        {preview.summary.totalRows === 1 ? '' : 's'} parsed
        {clientName ? ` · ${clientName}` : ''}
      </p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {cards.map((card) => (
          <div
            key={card.label}
            className={`rounded-lg border ${card.tone} px-4 py-3`}
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.18em]">
              {card.label}
            </p>
            <p className="mt-2 text-2xl font-bold tracking-tight tabular-nums">
              {card.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

interface PreviewTabProps {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  tone: 'emerald' | 'amber' | 'red';
}

const PreviewTab: React.FC<PreviewTabProps> = ({
  active,
  onClick,
  label,
  count,
  tone,
}) => {
  const toneActive =
    tone === 'emerald'
      ? 'bg-success-lighter text-success-base'
      : tone === 'amber'
        ? 'bg-warning-lighter text-warning-dark'
        : 'bg-error-lighter text-error-base';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-semibold tracking-tight transition-colors ${
        active
          ? 'bg-white text-text-strong-950 border border-stroke-soft-200'
          : 'text-text-sub-600 hover:text-text-strong-950 border border-transparent'
      }`}
    >
      {label}
      <span
        className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${
          active ? toneActive : 'bg-bg-soft-200 text-text-sub-600'
        }`}
      >
        {count}
      </span>
    </button>
  );
};

const ToCreateTable: React.FC<{
  preview: ImportPreviewResult;
  route: PreviewRoute | null;
}> = ({ preview, route }) => {
  // Track which AWB groups are expanded to reveal nested items[].
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (preview.summary.toCreateShipments === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-text-sub-600">
        No new shipments to create.
      </div>
    );
  }

  const toggle = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    // `contain: inline-size` keeps the table's min-content width from
    // propagating up and widening the whole modal on a narrow viewport: the
    // table scrolls sideways inside this box instead.
    <div className="max-h-[320px] overflow-auto [contain:inline-size]">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-bg-weak-50 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
          <tr>
            <th className="w-6 px-2 py-2" />
            <th className="px-3 py-2">Booking ID</th>
            <th className="px-3 py-2">Receiver</th>
            <th className="px-3 py-2">Destination</th>
            <th className="px-3 py-2">Service</th>
            <th className="px-3 py-2 text-right">Items</th>
            <th className="px-3 py-2 text-right">Koli</th>
            <th className="px-3 py-2 text-right">Total weight</th>
          </tr>
        </thead>
        <tbody className="text-text-strong-950">
          {preview.toCreate.map((row) => (
            <ShipmentGroupRow
              key={row.bookingId}
              row={row}
              route={route}
              expanded={!!expanded[row.bookingId]}
              onToggle={() => toggle(row.bookingId)}
            />
          ))}
        </tbody>
      </table>
      {preview.summary.toCreateShipments > preview.toCreate.length && (
        <div className="border-t border-stroke-soft-200 bg-bg-weak-50 px-3 py-2 text-[11px] text-text-sub-600">
          Showing first {preview.toCreate.length} of{' '}
          {preview.summary.toCreateShipments}.
        </div>
      )}
    </div>
  );
};

/**
 * Destination cell of a preview row. On a Titik Dropoff upload a row whose
 * dropoff was left blank lands AT the anchor — show that as the anchor's chip
 * (full address on hover) so it reads apart from a row keeping its own address.
 * Wraps (max two lines) rather than truncating on one line, so a long address
 * never becomes the table's min-content width.
 */
const DestinationCell: React.FC<{
  address: string;
  route: PreviewRoute | null;
}> = ({ address, route }) => {
  const toAnchor =
    route?.anchorRole === 'DESTINATION' &&
    route.anchorAddress != null &&
    address === route.anchorAddress;
  return (
    <td className="px-3 py-1.5">
      {toAnchor ? (
        <span
          title={address}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-accent/40 bg-accent/5 px-1.5 py-0.5 text-[10px] font-semibold text-accent"
        >
          <i className="ri-map-pin-2-line" aria-hidden="true" />
          {route?.anchorName ?? 'Hub / pitstop'}
        </span>
      ) : (
        <span
          className="line-clamp-2 min-w-[8rem] text-text-sub-600"
          title={address}
        >
          {address || '—'}
        </span>
      )}
    </td>
  );
};

const ShipmentGroupRow: React.FC<{
  row: ImportPreviewShipmentRow;
  route: PreviewRoute | null;
  expanded: boolean;
  onToggle: () => void;
}> = ({ row, route, expanded, onToggle }) => {
  return (
    <>
      <tr
        className="border-t border-stroke-soft-200 cursor-pointer hover:bg-bg-weak-50"
        onClick={onToggle}
      >
        <td className="px-2 py-1.5 text-text-soft-400">
          <i
            className={`text-[14px] ${
              expanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'
            }`}
          />
        </td>
        <td className="px-3 py-1.5 font-mono">{row.bookingId}</td>
        <td className="px-3 py-1.5">{row.receiverName}</td>
        <DestinationCell address={row.destinationAddress} route={route} />
        <td className="px-3 py-1.5">{row.serviceType}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{row.itemCount}</td>
        {/* `totalKoli` sums every item of the AWB — `items` below is capped by
            the BE preview cap, so don't reduce that array instead. */}
        <td className="px-3 py-1.5 text-right tabular-nums">
          {row.totalKoli ?? '—'}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums">
          {row.totalWeight}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-bg-weak-50/60">
          <td />
          <td colSpan={7} className="px-3 py-2">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
              Items
            </div>
            <table className="w-full text-left text-[11px]">
              <thead className="text-text-sub-600">
                <tr>
                  <th className="py-1 pr-3">Row</th>
                  <th className="py-1 pr-3">Item name</th>
                  <th className="py-1 pr-3 text-right">Koli</th>
                  <th className="py-1 pr-3 text-right">Weight</th>
                </tr>
              </thead>
              <tbody className="text-text-sub-600">
                {row.items.map((it) => (
                  <tr
                    key={`${row.bookingId}-${it.rowNumber}-${it.itemName}`}
                    className="border-t border-stroke-soft-200"
                  >
                    <td className="py-1 pr-3 tabular-nums text-text-sub-600">
                      {it.rowNumber}
                    </td>
                    <td className="py-1 pr-3 font-mono">{it.itemName}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">
                      {it.koli ?? '—'}
                    </td>
                    <td className="py-1 pr-3 text-right tabular-nums">
                      {it.weight}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
};

const DuplicatesTable: React.FC<{ preview: ImportPreviewResult }> = ({
  preview,
}) => {
  if (preview.summary.duplicates === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-text-sub-600">
        No duplicates found.
      </div>
    );
  }
  return (
    <div className="max-h-[280px] overflow-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-bg-weak-50 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
          <tr>
            <th className="px-3 py-2">Booking ID</th>
            <th className="px-3 py-2">Existing waybill</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2 text-right">Append</th>
            <th className="px-3 py-2 text-right">Skipped</th>
          </tr>
        </thead>
        <tbody className="text-text-strong-950">
          {preview.duplicates.map((d, idx) => (
            <tr
              key={`${d.bookingId}-${idx}`}
              className="border-t border-stroke-soft-200 align-top"
            >
              <td className="px-3 py-2 font-mono">{d.bookingId}</td>
              <td className="px-3 py-2 font-mono text-[11px] text-text-sub-600">
                {d.existingWaybill}
              </td>
              <td className="px-3 py-2">
                <span className="font-medium text-text-sub-600">
                  {d.existingStatus}
                </span>
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-success-base">
                {d.appendItemCount}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-text-sub-600">
                {d.skippedItemCount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ErrorsTable: React.FC<{
  preview: ImportPreviewResult;
  onDownload: () => void;
}> = ({ preview, onDownload }) => {
  if (preview.summary.errors === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-text-sub-600">
        No errors. You're good to go.
      </div>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between border-b border-stroke-soft-200 bg-bg-weak-50 px-3 py-2">
        <p className="text-[11px] text-text-sub-600">
          {preview.summary.errors} row error
          {preview.summary.errors === 1 ? '' : 's'} blocking commit.
        </p>
        <button
          type="button"
          onClick={onDownload}
          className="inline-flex items-center gap-1 text-xs font-semibold tracking-tight text-accent hover:underline"
        >
          <i className="ri-download-2-line text-[14px]" />
          Download error CSV
        </button>
      </div>
      <div className="max-h-[260px] overflow-auto">
        <table className="w-full text-left text-xs">
          <thead className="sticky top-0 bg-bg-weak-50 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
            <tr>
              <th className="px-3 py-2">Row</th>
              <th className="px-3 py-2">Field</th>
              <th className="px-3 py-2">Value</th>
              <th className="px-3 py-2">Reason</th>
            </tr>
          </thead>
          <tbody className="text-text-strong-950">
            {preview.errors.map((e, idx) => (
              <tr
                key={`${e.rowNumber}-${e.field}-${idx}`}
                className="border-t border-stroke-soft-200"
              >
                <td className="px-3 py-1.5 tabular-nums text-text-sub-600">
                  {e.rowNumber}
                </td>
                <td className="px-3 py-1.5 font-mono text-[11px]">{e.field}</td>
                <td className="px-3 py-1.5 font-mono text-[11px] text-text-sub-600">
                  {e.value || (
                    <span className="italic text-text-soft-400">empty</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-error-base">{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImportModal;
